import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { profileService } from "@/services/profileService";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  BookOpen, 
  Headphones, 
  PenTool, 
  Sparkles, 
  Target, 
  Layers, 
  CheckCircle2, 
  ArrowRight, 
  BarChart3, 
  Clock, 
  AlertCircle,
  Award,
  HelpCircle,
  FileText,
  Bookmark,
  Users,
  Compass,
  Check,
  Mail,
  Loader2
} from "lucide-react";

export default function LandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, signOut, loading } = useAuth();
  
  const [email, setEmail] = useState("");
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);

  const handleJoinWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    const finalEmail = user?.email || email;
    if (!finalEmail) return;

    setWaitlistLoading(true);
    try {
      await profileService.addToWaitlist(user?.id || "", finalEmail, "landing", "monthly");
      setWaitlistSuccess(true);
      setEmail("");
    } catch (err) {
      console.error("[LandingPage] Failed to join waitlist:", err);
    } finally {
      setWaitlistLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (err) {
      console.error("[LandingPage] Sign out failed:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-slate-50">
        <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-primary/10 border-t-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#F8F9FA] text-slate-800 flex flex-col font-sans overflow-x-hidden antialiased">
      {/* Dynamic Navigation Header */}
      <header className="w-full bg-white/80 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-50 shadow-2xs select-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center text-white font-extrabold text-lg shadow-sm">
                T
              </div>
              <span className="font-black text-slate-900 tracking-tight text-base">
                TEF Canada <span className="text-primary font-bold">Trainer</span>
              </span>
            </Link>
            <nav className="hidden md:flex items-center gap-5 text-xs font-bold text-slate-600">
              <Link to="/" className="hover:text-primary transition-colors">{t("common.home", "Home")}</Link>
              <Link to="/pricing" className="hover:text-primary transition-colors">{t("pricing.title", "Pricing")}</Link>
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-28 sm:w-32">
              <LanguageSwitcher />
            </div>
            
            {user ? (
              <div className="flex items-center gap-2">
                <Link to="/dashboard">
                  <Button size="sm" className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs shadow-xs px-4 py-2">
                    {t("onboarding.go_to_dashboard", "Go to Dashboard")}
                  </Button>
                </Link>
                <Button size="sm" variant="ghost" onClick={handleLogout} className="text-slate-500 hover:text-slate-800 font-bold text-xs">
                  {t("settings.logout", "Sair")}
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2.5">
                <Link to="/auth">
                  <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900 font-bold text-xs px-3">
                    {t("settings.login", "Entrar")}
                  </Button>
                </Link>
                <Link to="/auth?mode=signup" className="hidden sm:inline-block">
                  <Button size="sm" className="bg-primary hover:bg-primary/95 text-white font-black rounded-xl text-xs shadow-xs px-4 py-2">
                    {t("landing.btn_start_free_trial", "Start Free Trial")}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-20 pb-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-black shadow-3xs">
          <Sparkles className="w-3.5 h-3.5 fill-primary/20 animate-spin-slow" />
          <span>CE, CO & EE SMART PRACTICE</span>
        </div>

        <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-slate-900 max-w-4xl mx-auto leading-[1.08] pt-1">
          {t("landing.hero_title", "Prepare for TEF Canada with smarter practice")}
        </h1>

        <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto font-medium leading-relaxed">
          {t("landing.hero_subtitle", "Practice Reading, Listening and Writing with personalized feedback, error tracking, flashcards and study plans.")}
        </p>

        <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-3.5 max-w-md mx-auto">
          {user ? (
            <Link to="/dashboard" className="w-full">
              <Button className="w-full h-13 bg-primary hover:bg-primary/95 text-white font-black rounded-xl text-sm px-8 shadow-md flex items-center justify-center gap-1.5 transition-all transform hover:scale-[1.01]">
                <span>{t("landing.btn_go_to_dashboard", "Go to Dashboard")}</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/auth?mode=signup" className="w-full sm:w-auto flex-1">
                <Button className="w-full h-13 bg-primary hover:bg-primary/95 text-white font-black rounded-xl text-sm px-8 shadow-md flex items-center justify-center gap-1.5 transition-all transform hover:scale-[1.01]">
                  <span>{t("landing.btn_start_free_trial", "Start Free Trial")}</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/pricing" className="w-full sm:w-auto flex-1">
                <Button variant="outline" className="w-full h-13 bg-white border-slate-200 text-slate-700 font-bold rounded-xl text-sm px-8 hover:bg-slate-50 shadow-2xs transition-all">
                  <span>{t("landing.btn_view_premium", "View Premium")}</span>
                </Button>
              </Link>
            </>
          )}
        </div>
      </section>

      {/* Main Benefits Cards */}
      <section className="py-20 bg-white border-y border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-3xl mx-auto space-y-2">
            <h2 className="text-3xl font-black text-slate-900 tracking-tight leading-tight">
              {t("landing.benefits_title", "Core Benefits")}
            </h2>
            <p className="text-slate-500 text-sm font-medium">
              Everything you need in a single platform, powered by modern learning models.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: t("landing.benefit1_title", "Realistic TEF practice"), desc: t("landing.benefit1_desc", "High-fidelity mock tests structured strictly to exam regulations."), icon: FileText, color: "text-purple-600 bg-purple-50" },
              { title: t("landing.benefit2_title", "AI-powered writing feedback"), desc: t("landing.benefit2_desc", "Receive immediate grade estimates and structured pedagogical feedback."), icon: Sparkles, color: "text-amber-600 bg-amber-50" },
              { title: t("landing.benefit3_title", "Error Bank"), desc: t("landing.benefit3_desc", "Save mistakes automatically and review them to avoid recurrences."), icon: Award, color: "text-red-600 bg-red-50" },
              { title: t("landing.benefit4_title", "Spaced Repetition Flashcards"), desc: t("landing.benefit4_desc", "Memorize words and connectors in the optimal learning intervals."), icon: Bookmark, color: "text-blue-600 bg-blue-50" },
              { title: t("landing.benefit5_title", "Weakness Map"), desc: t("landing.benefit5_desc", "Visualize exactly which competencies need correction and reinforcement."), icon: Target, color: "text-rose-600 bg-rose-50" },
              { title: t("landing.benefit6_title", "Personalized study plan"), desc: t("landing.benefit6_desc", "Dynamic daily targets designed by 'Coach TEF' to maximize scores."), icon: Clock, color: "text-emerald-600 bg-emerald-50" }
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <Card key={idx} className="border border-slate-200/60 bg-white rounded-2xl shadow-2xs hover:border-primary/20 hover:shadow-xs transition-all duration-300 flex flex-col justify-between">
                  <CardHeader className="p-6 space-y-3">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shadow-3xs shrink-0 ${item.color}`}>
                      <Icon className="w-5.5 h-5.5" />
                    </div>
                    <CardTitle className="text-base font-extrabold text-slate-900">{item.title}</CardTitle>
                    <p className="text-xs text-slate-500 leading-relaxed font-semibold">{item.desc}</p>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it Works Step Sections */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-12">
        <div className="text-center max-w-3xl mx-auto space-y-2">
          <h2 className="text-3xl font-black text-slate-900 tracking-tight leading-tight">
            {t("landing.how_it_works_title", "How it Works")}
          </h2>
          <p className="text-slate-500 text-sm font-medium">Follow our structured learning progression to scale your language level.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { step: "1", title: t("landing.step1_title", "Take a test"), desc: t("landing.step1_desc", "Complete high-fidelity simulations for CE, CO, or EE."), icon: FileText, color: "text-purple-600 bg-purple-50/50" },
            { step: "2", title: t("landing.step2_title", "Get feedback"), desc: t("landing.step2_desc", "Immediately receive detailed reports and NCLC level estimates."), icon: Sparkles, color: "text-indigo-600 bg-indigo-50/50" },
            { step: "3", title: t("landing.step3_title", "Review your errors"), desc: t("landing.step3_desc", "Log mistakes in grammar and vocabulary to the active bank."), icon: Award, color: "text-amber-600 bg-amber-50/50" },
            { step: "4", title: t("landing.step4_title", "Follow your study plan"), desc: t("landing.step4_desc", "Follow personalized daily study target recommendations."), icon: Compass, color: "text-emerald-600 bg-emerald-50/50" }
          ].map((item, idx) => {
            const Icon = item.icon;
            return (
              <Card key={idx} className="border border-slate-200/60 bg-white rounded-2xl shadow-3xs p-6 text-center relative overflow-hidden flex flex-col justify-between">
                <div className="absolute top-0 right-0 w-12 h-12 bg-primary/5 rounded-bl-3xl flex items-center justify-center font-black text-primary text-sm shadow-3xs">
                  {item.step}
                </div>
                <div className="space-y-3 pt-2">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mx-auto shadow-4xs ${item.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900">{item.title}</h3>
                  <p className="text-xs text-slate-500 leading-normal font-semibold max-w-[200px] mx-auto">{item.desc}</p>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Who is it For Segment */}
      <section className="py-20 bg-white border-y border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-left">
              <Badge className="bg-primary/10 text-primary border-none font-extrabold px-3 py-1 rounded-full text-xs">
                <Users className="w-3.5 h-3.5 mr-1" />
                Designed For Success
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight leading-none">
                {t("landing.who_is_it_for_title", "Who is it for?")}
              </h2>
              <p className="text-slate-500 text-sm font-semibold max-w-md">
                Our tools are optimized for serious applicants aiming for Canada's immigration programs.
              </p>

              <div className="grid gap-3 pt-2">
                {[
                  t("landing.who_item1", "Candidates preparing for TEF Canada"),
                  t("landing.who_item2", "Immigration applicants targeting NCLC 7+"),
                  t("landing.who_item3", "Learners who need structured practice"),
                  t("landing.who_item4", "Students who want to track progress")
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 font-semibold text-slate-700 text-xs">
                    <div className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 border border-emerald-100 shadow-3xs">
                      <Check className="w-3 h-3 stroke-[3]" />
                    </div>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Premium trial preview card */}
            <div className="bg-gradient-to-br from-primary/5 via-white to-purple-50 border border-primary/10 rounded-3xl p-6 sm:p-8 shadow-inner text-left space-y-5">
              <div className="space-y-1">
                <h3 className="text-lg font-extrabold text-slate-900 leading-snug">
                  {t("landing.trial_title", "Your Free Trial Period")}
                </h3>
                <p className="text-xs text-slate-500 font-semibold leading-relaxed">
                  {t("landing.trial_subtitle", "Try the platform before subscribing.")}
                </p>
              </div>

              <div className="border-t border-slate-100 pt-4 grid gap-3">
                {[
                  { label: t("landing.trial_include_tests", "Limited mock tests"), limit: "4 CE/CO" },
                  { label: t("landing.trial_include_corrections", "Limited writing corrections"), limit: "3 EE" },
                  { label: t("landing.trial_include_flashcards", "Flashcards preview"), limit: "Up to 50" },
                  { label: t("landing.trial_include_errors", "Error bank preview"), limit: "Up to 50" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100 shadow-2xs">
                    <span className="text-xs font-bold text-slate-700">{item.label}</span>
                    <Badge variant="outline" className="text-[10px] font-extrabold bg-primary/10 text-primary border-none px-2.5 py-0.5 uppercase tracking-wider">
                      {item.limit}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Public Interest Waitlist Capture CTA Section */}
      <section className="py-20 bg-primary/5 border-b border-primary/10">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 leading-tight">
            {t("landing.cta_title", "Start your TEF preparation today")}
          </h2>
          <p className="text-slate-500 text-sm max-w-xl mx-auto font-semibold leading-relaxed">
            Register your email below to get early updates and waiting list priority for the complete Premium unlimited suite.
          </p>

          <Card className="max-w-md mx-auto rounded-2xl border-none shadow-md overflow-hidden bg-white mt-4">
            <CardHeader className="bg-primary/5 border-b border-primary/10 py-4.5 text-center">
              <CardTitle className="text-xs font-black text-primary uppercase tracking-wider flex items-center justify-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-primary shrink-0" />
                {t("landing.waitlist_title", "Premium Waitlist")}
              </CardTitle>
              <CardDescription className="text-[10px] text-slate-500 mt-0.5 font-semibold">
                {t("landing.waitlist_desc", "Join our waitlist to be notified first on release.")}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-5">
              {waitlistSuccess ? (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-center animate-in fade-in zoom-in-95 duration-300">
                  <span className="text-xs font-black text-emerald-800 block">
                    ✓ {t("landing.waitlist_success", "You're on the waitlist.")}
                  </span>
                  <p className="text-[10px] text-emerald-600 mt-0.5 font-semibold">
                    {t("upgrade.interestSuccess", "We'll notify you with exclusive details shortly.")}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleJoinWaitlist} className="space-y-3">
                  {!user && (
                    <input
                      type="email"
                      required
                      placeholder={t("landing.waitlist_placeholder", "Enter your email")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-xs focus:outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all bg-white font-medium text-center"
                    />
                  )}
                  {user && (
                    <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-600 mb-1">
                      {user.email}
                    </div>
                  )}
                  <Button 
                    type="submit" 
                    disabled={waitlistLoading}
                    className="w-full py-4.5 rounded-xl font-bold text-xs bg-primary hover:bg-primary/95 text-white flex items-center justify-center gap-1.5 shadow-xs"
                  >
                    {waitlistLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-white" /> : null}
                    <span>{t("landing.waitlist_btn", "Join Waitlist")}</span>
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Public Footer */}
      <footer className="w-full bg-white border-t border-slate-200 py-12 px-4 sm:px-6 lg:px-8 mt-auto select-none">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="space-y-1.5">
            <div className="flex items-center justify-center md:justify-start gap-2.5">
              <div className="w-6 h-6 rounded-lg bg-primary flex items-center justify-center text-white font-black text-xs shadow-4xs">
                T
              </div>
              <span className="font-black text-slate-900 tracking-tight text-xs">
                TEF Canada Trainer
              </span>
            </div>
            <p className="text-[10px] text-slate-500 font-semibold">Independent practice environment for TEF Canada applicants.</p>
          </div>

          <div className="flex items-center gap-6 text-xs font-bold text-slate-500">
            <Link to="/" className="hover:text-primary transition-colors">{t("common.home", "Home")}</Link>
            <Link to="/pricing" className="hover:text-primary transition-colors">{t("pricing.title", "Pricing")}</Link>
            {!user && <Link to="/auth" className="hover:text-primary transition-colors">{t("settings.login", "Login")}</Link>}
            {user && <Link to="/dashboard" className="hover:text-primary transition-colors">{t("onboarding.go_to_dashboard", "Dashboard")}</Link>}
          </div>

          <p className="text-[10px] text-slate-400 font-semibold max-w-xs leading-normal">
            This platform is not officially associated with or endorsed by CCI Paris Île-de-France or IRCC.
          </p>
        </div>
      </footer>
    </div>
  );
}
